import React, { Suspense, lazy, useMemo } from 'react';
import { LayoutBlockType } from '@lnr-v2/types';
import type {
  LayoutBlock,
  HeroBlock as HeroBlockType,
  CaseDetail,
} from '@lnr-v2/types';
import { ErrorBoundary } from '../../atoms';
import { getTransformedImageUrl } from '../../../utils/imageHelpers';
import styles from './DynamicLayoutRenderer.module.css';
import { AwardsSection } from '../AwardsSection/AwardsSection';

// HeroBlock is NOT lazy loaded - it's critical for LCP (above the fold)
import { HeroBlock } from '../../molecules/blocks/HeroBlock';
const TextBlock = lazy(() =>
  import('../../molecules/blocks/TextBlock').then((m) => ({
    default: m.TextBlock,
  }))
);
const ImageFullBlock = lazy(() =>
  import('../../molecules/blocks/ImageFullBlock').then((m) => ({
    default: m.ImageFullBlock,
  }))
);
const ImageGridBlock = lazy(() =>
  import('../../molecules/blocks/ImageGridBlock').then((m) => ({
    default: m.ImageGridBlock,
  }))
);
const VideoBlock = lazy(() =>
  import('../../molecules/blocks/VideoBlock').then((m) => ({
    default: m.VideoBlock,
  }))
);
const Columns2Block = lazy(() =>
  import('../../molecules/blocks/Columns2Block').then((m) => ({
    default: m.Columns2Block,
  }))
);
const SpacerBlock = lazy(() =>
  import('../../molecules/blocks/SpacerBlock').then((m) => ({
    default: m.SpacerBlock,
  }))
);
const CaseInfoBlock = lazy(() =>
  import('../../molecules/blocks/CaseInfoBlock').then((m) => ({
    default: m.CaseInfoBlock,
  }))
);
const MotionBlock = lazy(() =>
  import('../../molecules/blocks/MotionBlock').then((m) => ({
    default: m.MotionBlock,
  }))
);

interface DynamicLayoutRendererProps {
  layout: LayoutBlock[];
  caseDetail?: CaseDetail; // For passing bilingual data to blocks
}

/**
 * Block loading skeleton component
 */
const BlockSkeleton: React.FC<{ type: LayoutBlockType }> = ({ type }) => {
  switch (type) {
    case LayoutBlockType.HERO:
      return <div className={`${styles.skeleton} ${styles.skeletonHero}`} />;

    case LayoutBlockType.TEXT:
      return <div className={`${styles.skeleton} ${styles.skeletonText}`} />;

    case LayoutBlockType.IMAGE_FULL:
      return (
        <div className={`${styles.skeleton} ${styles.skeletonImageFull}`} />
      );

    case LayoutBlockType.IMAGE_GRID:
      return (
        <div className={styles.skeletonImageGrid}>
          <div className={styles.skeletonImageGridInner}>
            <div className={styles.skeletonImageGridItem} />
            <div className={styles.skeletonImageGridItem} />
            <div className={styles.skeletonImageGridItem} />
            <div className={styles.skeletonImageGridItem} />
          </div>
        </div>
      );

    case LayoutBlockType.VIDEO:
      return <div className={`${styles.skeleton} ${styles.skeletonVideo}`} />;

    case LayoutBlockType.COLUMNS_2:
      return (
        <div className={styles.skeletonColumns2}>
          <div className={styles.skeletonColumns2Item} />
          <div className={styles.skeletonColumns2Item} />
        </div>
      );

    case LayoutBlockType.SPACER:
      return <div className={styles.skeletonSpacer} />;

    case LayoutBlockType.CASE_INFO:
      return (
        <div className={`${styles.skeleton} ${styles.skeletonCaseInfo}`} />
      );

    case LayoutBlockType.MOTION:
      return <div className={`${styles.skeleton} ${styles.skeletonMotion}`} />;

    default:
      return <div className={styles.skeleton} style={{ height: 100 }} />;
  }
};

/**
 * Render a single block with lazy loading
 */
const renderBlock = (
  block: LayoutBlock,
  caseDetail?: CaseDetail
): React.ReactNode => {
  switch (block.type) {
    case LayoutBlockType.HERO:
      return <HeroBlock block={block} />;

    case LayoutBlockType.TEXT:
      return <TextBlock block={block} />;

    case LayoutBlockType.IMAGE_FULL:
      return <ImageFullBlock block={block} />;

    case LayoutBlockType.IMAGE_GRID:
      return <ImageGridBlock block={block} />;

    case LayoutBlockType.VIDEO:
      return <VideoBlock block={block} />;

    case LayoutBlockType.COLUMNS_2:
      return <Columns2Block block={block} />;

    case LayoutBlockType.SPACER:
      return <SpacerBlock block={block} />;

    case LayoutBlockType.CASE_INFO:
      return <CaseInfoBlock block={block} caseDetail={caseDetail} />;

    case LayoutBlockType.MOTION:
      return <MotionBlock block={block} />;

    default:
      return null;
  }
};

/**
 * Extract hero image URL for preloading
 */
const getHeroImageUrl = (block: HeroBlockType): string | null => {
  const image = block.content?.image;
  if (!image) return null;

  // CDN format
  if ('url' in image && image.url) return image.url;
  // Legacy format
  if ('full' in image && image.full) return image.full;

  return null;
};

/**
 * Dynamic Layout Renderer
 *
 * Renders an array of layout blocks with:
 * - HeroBlock loaded synchronously (critical for LCP)
 * - React.lazy code splitting for other block types
 * - Suspense with skeleton fallbacks
 * - Maintains layout stability during load
 */
export const DynamicLayoutRenderer: React.FC<DynamicLayoutRendererProps> = ({
  layout,
  caseDetail,
}) => {
  // Find hero block for preloading
  const heroBlock = layout?.find((b) => b.type === LayoutBlockType.HERO) as
    | HeroBlockType
    | undefined;
  const rawHeroImageUrl = heroBlock ? getHeroImageUrl(heroBlock) : null;
  const heroImageUrl = getTransformedImageUrl(rawHeroImageUrl);

  // Memoize the rendered blocks to prevent unnecessary re-renders
  const renderedBlocks = useMemo(() => {
    if (!layout || layout.length === 0) {
      return null;
    }

    let awardsRendered = false;

    return layout.map((block) => {
      // HeroBlock is rendered directly without Suspense (not lazy loaded)
      if (block.type === LayoutBlockType.HERO) {
        const shouldRenderAwardsAfterHero =
          !awardsRendered && Boolean(caseDetail?.awards?.length);
        awardsRendered = true;

        return (
          <div
            key={block.id}
            className={`${styles.blockWrapper} ${styles.heroBlock}`}
          >
            <ErrorBoundary
              fallback={
                <div className={styles.errorBlock}>
                  Failed to load content block
                </div>
              }
            >
              <HeroBlock block={block as HeroBlockType} />
            </ErrorBoundary>
            {shouldRenderAwardsAfterHero && (
              <AwardsSection awards={caseDetail?.awards} />
            )}
          </div>
        );
      }

      // Other blocks use lazy loading with Suspense
      return (
        <div key={block.id} className={styles.blockWrapper}>
          <div className={styles.blockContent}>
            <ErrorBoundary
              fallback={
                <div className={styles.errorBlock}>
                  Failed to load content block
                </div>
              }
            >
              <Suspense fallback={<BlockSkeleton type={block.type} />}>
                {renderBlock(block, caseDetail)}
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
      );
    });
  }, [layout, caseDetail]);

  if (!layout || layout.length === 0) {
    return null;
  }

  return (
    <>
      {/* Preload hero image for faster LCP */}
      {heroImageUrl && <link rel="preload" as="image" href={heroImageUrl} />}
      <main className={styles.dynamicLayout}>{renderedBlocks}</main>
    </>
  );
};
